import z from 'zod';
import { ERROR_MESSAGES } from '~/constants';
import {
  addressSchema,
  facilitySchemaRequiredNoAddress,
  optionSchema,
  optionSchemaRequired,
} from '~/features/types';

/** [MCV-1124] Strict YYYY-MM-DD check that validates real calendar dates
 *  (e.g. rejects "12/12/54545", "2025-02-30"). Kept local for now; if more
 *  features need this, promote to `~/features/utils`. */
const isStrictIsoDate = (value: string): boolean => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  );
};

const MIN_CREDENTIAL_DATE = '1915-01-01';
const MIN_CREDENTIAL_DATE_MESSAGE = `Date must be on or after ${MIN_CREDENTIAL_DATE}`;
const isOnOrAfterMinCredentialDate = (value: string | null | undefined): boolean =>
  !value || value >= MIN_CREDENTIAL_DATE;

/** [MCV-1124] Required ISO date with split messages: empty → "Required",
 *  malformed → "Invalid date". */
const requiredIsoDate = z
  .string()
  .min(1, { message: ERROR_MESSAGES.REQUIRED })
  .superRefine((value, ctx) => {
    if (!value) return;
    if (!isStrictIsoDate(value)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: ERROR_MESSAGES.INVALID_DATE });
      return;
    }
    if (!isOnOrAfterMinCredentialDate(value)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: MIN_CREDENTIAL_DATE_MESSAGE });
    }
  });

const optionalIsoDate = z
  .union([z.iso.date(ERROR_MESSAGES.INVALID_DATE), z.literal(''), z.null()])
  .optional()
  .nullable()
  .refine(isOnOrAfterMinCredentialDate, { message: MIN_CREDENTIAL_DATE_MESSAGE });

const US_ZIP = /^\d{5}(?:[-\s]\d{4})?$/;

export const institutionalSchema = z
  .object({
    name: optionSchemaRequired,
    addressLine1: z
      .string({ error: ERROR_MESSAGES.REQUIRED })
      .trim()
      .min(1, ERROR_MESSAGES.REQUIRED)
      .max(47, ERROR_MESSAGES.MAX_CHARACTER_LIMIT(47)),
    // MCV-693: allow undefined so a missing addressLine2 default doesn't silently fail validation
    // with a vague "Expected string, received undefined" that points users away from the real issue.
    addressLine2: z.string().max(47, ERROR_MESSAGES.MAX_CHARACTER_LIMIT(47)).optional(),
    city: z
      .string({ error: ERROR_MESSAGES.REQUIRED })
      .trim()
      .min(1, ERROR_MESSAGES.REQUIRED)
      .max(50, ERROR_MESSAGES.MAX_CHARACTER_LIMIT(50)),
    state: optionSchemaRequired,
    country: optionSchemaRequired,
    postalCode: z.string({ error: ERROR_MESSAGES.REQUIRED }).trim().min(1, ERROR_MESSAGES.REQUIRED),
  })
  .superRefine((institution, ctx) => {
    if (
      institution.country.value === 'US' &&
      institution.postalCode &&
      !US_ZIP.test(institution.postalCode)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['postalCode'],
        message: ERROR_MESSAGES.INVALID_ZIP_CODE,
      });
    }
  });

export const detailSchema = z.object({
  _id: z.number().optional(),
  _education_id: z.number().optional(),
  fieldLabel: optionSchemaRequired,
  fieldTxt: z
    .string()
    .min(1, 'Detail text is required')
    .max(4096, ERROR_MESSAGES.MAX_CHARACTER_LIMIT(4096)),
});

export const educationSchema = z
  .object({
    id: z.number(),
    // [MCV-1208] Require ISO date format (matches trainingSchema pattern). Without this,
    // malformed input like "12/12/54545" silently slipped into the submission path and
    // surfaced as a vague corner toast from the server. Now caught inline at the field.
    // [MCV-1124] Split empty-vs-malformed for clearer messages.
    startDate: requiredIsoDate,
    endDate: requiredIsoDate,
    graduatedFlag: z.boolean(),
    graduationDate: optionalIsoDate,
    institution: institutionalSchema,
    degree: optionSchema,
    fieldOfStudy: z
      .string()
      .max(255, ERROR_MESSAGES.MAX_CHARACTER_LIMIT(255))
      .optional()
      .nullable(),
    details: z.array(detailSchema),
    validFlag: z.boolean(),
    warnings: z.record(z.string(), z.string()).optional(),
    explanationTx: z
      .string()
      .max(4096, ERROR_MESSAGES.MAX_CHARACTER_LIMIT(4096))
      .optional()
      .nullable(),
  })
  .refine(
    (schema) => {
      if (!schema.startDate || !schema.endDate) return true;
      return schema.startDate < schema.endDate;
    },
    { message: ERROR_MESSAGES.INVALID_START_END_DATES, path: ['startDate'] }
  )
  .refine(
    (schema) => {
      return (
        !schema.graduatedFlag ||
        (schema.graduatedFlag && schema.graduationDate && schema.graduationDate >= schema.endDate)
      );
    },
    {
      message: '"Graduation Date" must be after or the same as "End Date"',
      path: ['graduationDate'],
    }
  );

export const medicalBoardSchema = z.object({
  label: z.string(),
  value: z.string().min(1, ERROR_MESSAGES.REQUIRED),
  id: z.string().optional(),
});

export const specialtyBoardSchema = z.object({
  id: z.string().optional(),
  label: z.string(),
  value: z.string().min(1, ERROR_MESSAGES.REQUIRED),
});
export const specialtyCertSchema = z.object({
  id: z.string().optional(),
  label: z.string(),
  value: z.string().min(1, ERROR_MESSAGES.REQUIRED),
});

export const subSpecialtyCertSchema = z.object({
  id: z.string().optional(),
  label: z.string(),
  value: z.string(),
});

// Internal comparison data, not editable form input. Preserve its typed value
// unchanged even when a stored facility contains invalid legacy fields; only
// the visible facility fields should participate in submission validation.
const facilityReferenceSchema = z
  .custom<z.infer<typeof facilitySchemaRequiredNoAddress>>()
  .optional();

export const trainingSchema = z
  .object({
    completedFlag: z.boolean(),
    completionDate: optionalIsoDate,
    endDate: optionalIsoDate,
    explanationTx: z.string().max(4096, ERROR_MESSAGES.MAX_CHARACTER_LIMIT(4096)),
    facility: facilitySchemaRequiredNoAddress,
    facilityReference: facilityReferenceSchema,
    id: z.number(),
    medicalBoard: medicalBoardSchema,
    specialtyBoard: specialtyBoardSchema,
    specialtyCertification: specialtyCertSchema,
    startDate: requiredIsoDate,
    // MCV-692: react-select emits `null` when the user clears the field, and zod's
    // bare `.optional()` only accepts `undefined`. That mismatch silently blocked
    // Save on edit for trainings where the user removed a previously-set
    // subspecialty. `.nullable()` widens the accepted set to include `null`.
    subSpecialtyCertification: subSpecialtyCertSchema.nullable().optional(),
    trainingType: optionSchema,
    validFlag: z.boolean().default(true).optional(),
    warnings: z.record(z.string(), z.string()).optional(),
  })
  .refine(
    (data) => {
      return !data.endDate || data.startDate < data.endDate;
    },
    { message: ERROR_MESSAGES.INVALID_START_END_DATES, path: ['startDate'] }
  )
  .refine(
    (data) => {
      return (
        !data.completedFlag ||
        !data.endDate ||
        (data.completedFlag &&
          data.completionDate &&
          data.endDate &&
          data.completionDate >= data.endDate)
      );
    },
    { message: 'Completion date must be after or the same as End Date', path: ['completionDate'] }
  );

export const TypeAndBoardSchema = z.object({
  trainingType: optionSchema,
  medicalBoard: medicalBoardSchema,
});

export const specialtyBoardFormSchema = z.object({
  specialtyBoard: specialtyBoardSchema,
  specialtyCertification: specialtyCertSchema,
  // MCV-692: stay in sync with trainingSchema — react-select emits `null` on clear.
  subSpecialtyCertification: subSpecialtyCertSchema.nullable().optional(),
});

export const facilityFormSchema = z
  .object({
    facility: facilitySchemaRequiredNoAddress,
    facilityReference: facilityReferenceSchema,
  })
  .superRefine((values, ctx) => {
    // Inline creation persists on Next. Require its address before any POST,
    // while selected facilities keep the existing legacy-address tolerance.
    if (values.facility.userAdded && !values.facility.value) {
      const address = addressSchema.safeParse(values.facility.address);
      if (!address.success) {
        for (const issue of address.error.issues) {
          ctx.addIssue({ ...issue, path: ['facility', 'address', ...issue.path] });
        }
      }
    }
    // [MCV-1372] Mirror the required-error to facility.value so AsyncSelect's
    // hardcoded errorPath (`${name}.value`) renders the inline message.
    // facilitySchema.label.min(1) already blocks submission, but it lands at
    // facility.label — invisible to the field component.
    if (!values.facility.label || !values.facility.label.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Facility Name is required',
        path: ['facility', 'value'],
      });
    }
  });

export const dateFormSchema = z
  .object({
    startDate: requiredIsoDate,
    endDate: optionalIsoDate,
    completedFlag: z.boolean(),
    completionDate: optionalIsoDate,
    explanationTx: z.string().max(4096, ERROR_MESSAGES.MAX_CHARACTER_LIMIT(4096)),
  })
  .refine(
    (data) => {
      return !data.endDate || data.startDate < data.endDate;
    },
    { message: '"Start Date" must be before "End Date"', path: ['startDate'] }
  )
  .refine(
    (data) => {
      return (
        !data.completedFlag ||
        (data.completedFlag &&
          data.completionDate &&
          data.endDate &&
          data.completionDate >= data.endDate)
      );
    },
    { message: 'Completion date must be after or the same as end date', path: ['completionDate'] }
  );
